import React, { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { CloseIcon, SpinnerIcon, DocumentsIcon } from "../../../Icons";
import DocumentFileDropzone from "./DocumentFileDropzone";
import {
  DEFAULT_DOCUMENT_MAX_BYTES,
  formatMaxFileSizeLabel,
  useDocumentFileUpload,
} from "../hooks/useDocumentFileUpload";
import {
  DOCUMENT_BTN,
  DOCUMENT_FIELD,
  DOCUMENT_MODAL,
  DOCUMENT_THEME,
} from "../documentTheme";
import {
  TAX_UPLOAD_ACCEPT,
  TAX_UPLOAD_EXTENSIONS,
  TAX_UPLOAD_TYPES_LABEL,
  isAllowedUploadExtension,
  isNonPreviewableDocument,
} from "../documentUploadTypes";

const TAX_FORM_TYPES = ["w_4", "i_9", "w_9", "edd", "direct_deposit"];

const buildTaxFormSchema = (maxBytes) =>
  z
    .object({
      type: z.enum(TAX_FORM_TYPES, {
        required_error: "Form type is required",
        invalid_type_error: `Type must be one of: ${TAX_FORM_TYPES.join(", ")}`,
      }),
      fileBase64: z
        .string({
          required_error: "Document is required",
          invalid_type_error: "Document is required",
        })
        .min(1, "Document is required")
        .max(
          maxBytes,
          `File must be at most ${formatMaxFileSizeLabel(maxBytes)}`,
        ),
      fileName: z
        .string({
          required_error: "Document is required",
          invalid_type_error: "Document is required",
        })
        .min(1, "Document is required"),
      notes: z.string().optional(),
    })
    .refine(
      (data) => isAllowedUploadExtension(data.fileName, TAX_UPLOAD_EXTENSIONS),
      {
        message: `Allowed file types: ${TAX_UPLOAD_EXTENSIONS.join(", ")}`,
        path: ["fileName"],
      },
    );

const TaxFormModal = ({
  isOpen,
  onClose,
  onSave,
  mode,
  initialData,
  getTaxFormById,
  maxFileBytes = DEFAULT_DOCUMENT_MAX_BYTES,
}) => {
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  const taxFormRequestSchema = useMemo(
    () => buildTaxFormSchema(maxFileBytes),
    [maxFileBytes],
  );

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors, isSubmitting },
    setError,
    clearErrors,
  } = useForm({
    resolver: zodResolver(taxFormRequestSchema),
    defaultValues: {
      type: "w_4",
      fileBase64: "",
      fileName: "",
      notes: "",
    },
    mode: "onBlur",
  });

  const fileName = watch("fileName");

  useEffect(() => {
    const loadTaxFormData = async () => {
      if (!isOpen) return;

      setPreviewUrl(null);

      if (mode === "edit" && initialData && getTaxFormById) {
        const targetId = initialData.targetDetailId || initialData.id;
        if (targetId) {
          setIsLoadingData(true);
          try {
            const response = await getTaxFormById(targetId);
            if (response && response.status === 1) {
              const taxForm =
                response.result?.form ||
                response.result?.taxAndPayroll ||
                response.result ||
                response;
              const previewSource =
                taxForm.fileUrl || taxForm.imageS3Url || null;
              const hasExistingFile = Boolean(
                previewSource || taxForm.imageS3Key,
              );
              reset({
                type: taxForm.type || "w_4",
                fileBase64: hasExistingFile ? "EXISTING_FILE_PLACEHOLDER" : "",
                fileName: hasExistingFile ? "Existing Document" : "",
                notes: taxForm.comments || "",
              });
              if (previewSource) {
                setPreviewUrl(previewSource);
              }
            }
          } catch (error) {
            console.error("Error loading tax form data:", error);
          } finally {
            setIsLoadingData(false);
          }
          return;
        }
      }

      if (initialData) {
        let latestDetail = initialData;
        if (initialData.details && initialData.details.length > 0) {
          latestDetail = initialData.details.reduce((prev, current) =>
            new Date(prev.createdAt) > new Date(current.createdAt)
              ? prev
              : current,
          );
        }

        const incomingType =
          latestDetail.formKey ||
          latestDetail.type ||
          initialData.formKey ||
          initialData.type ||
          "w_4";
        const validType = TAX_FORM_TYPES.includes(incomingType)
          ? incomingType
          : "w_4";
        reset({
          type: validType,
          fileBase64:
            mode === "edit" && latestDetail.imageS3Url
              ? "EXISTING_FILE_PLACEHOLDER"
              : "",
          fileName:
            mode === "edit" && latestDetail.imageS3Url
              ? "Existing Document"
              : "",
          notes: latestDetail.comments || "",
        });
        if (mode === "edit" && latestDetail.imageS3Url) {
          setPreviewUrl(latestDetail.imageS3Url);
        }
      } else {
        reset({
          type: "w_4",
          fileBase64: "",
          fileName: "",
          notes: "",
        });
      }
    };

    loadTaxFormData();
  }, [isOpen, initialData, mode, reset, getTaxFormById]);

  const { handleFileChange, handleFileDrop, handleDragOver, clearFile, maxBytes } = useDocumentFileUpload({
    maxBytes: maxFileBytes,
    setValue,
    setPreviewUrl,
    setError,
    clearErrors,
  });

  const onSubmit = async (data) => {
    try {
      const saved = await onSave(data);
      if (saved) {
        onClose();
      }
    } catch (error) {
      console.error("Submission error", error);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className={DOCUMENT_MODAL.backdrop}
      role="dialog"
      aria-modal="true"
      aria-labelledby="tax-form-modal-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !isSubmitting) onClose?.();
      }}
    >
      <div className={DOCUMENT_MODAL.panel}>
        <div className={DOCUMENT_MODAL.head}>
          <div className="min-w-0">
            <h3 id="tax-form-modal-title" className={DOCUMENT_MODAL.title}>
              {mode === "create" ? "Add New Tax Form" : "Edit Tax Form"}
              {isLoadingData ? (
                <SpinnerIcon className="inline animate-spin ml-2 h-4 w-4 text-indigo-500" />
              ) : null}
            </h3>
            <p className={DOCUMENT_MODAL.subtitle}>
              Upload a tax document and optional notes
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className={DOCUMENT_MODAL.close}
            aria-label="Close"
          >
            <CloseIcon size={14} />
          </button>
        </div>

        <div className={DOCUMENT_MODAL.body}>
          {previewUrl ? (
            <div className="border border-indigo-100 rounded-xl overflow-hidden bg-indigo-50/40 flex justify-center">
              {isNonPreviewableDocument(previewUrl, fileName) ? (
                <div className="p-4 text-center">
                  <DocumentsIcon className="mx-auto h-10 w-10 text-indigo-500 mb-2" />
                  <p className="text-sm text-slate-600 font-medium">
                    Preview not available for this file type
                  </p>
                  <a
                    href={previewUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`text-xs mt-1 block ${DOCUMENT_THEME.button.link}`}
                  >
                    Open file in new tab
                  </a>
                </div>
              ) : (
                <img
                  src={previewUrl}
                  alt="Tax Form Preview"
                  className="max-h-40 object-contain"
                />
              )}
            </div>
          ) : null}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
            <div>
              <label htmlFor="type" className={DOCUMENT_FIELD.label}>
                Form Type
              </label>
              <select
                id="type"
                {...register("type")}
                disabled
                className={DOCUMENT_FIELD.inputDisabled}
              >
                <option value="w_4">W-4</option>
                <option value="i_9">I-9</option>
                <option value="w_9">W-9</option>
                <option value="edd">EDD</option>
                <option value="direct_deposit">Direct Deposit</option>
              </select>
              {errors.type ? (
                <p className={DOCUMENT_FIELD.error}>{errors.type.message}</p>
              ) : null}
            </div>

            <div>
              <label htmlFor="notes" className={DOCUMENT_FIELD.label}>
                Notes
              </label>
              <textarea
                id="notes"
                rows={3}
                {...register("notes")}
                className={`${DOCUMENT_FIELD.textarea} ${errors.notes ? "border-red-400" : ""}`}
                placeholder="Additional details..."
              />
              {errors.notes ? (
                <p className={DOCUMENT_FIELD.error}>{errors.notes.message}</p>
              ) : null}
            </div>

            <div>
              <label className={DOCUMENT_FIELD.label}>Document</label>
              <DocumentFileDropzone
                inputId="tax-file-upload"
                accept={TAX_UPLOAD_ACCEPT}
                typesLabel={TAX_UPLOAD_TYPES_LABEL}
                maxBytes={maxBytes}
                fileName={fileName}
                onFileChange={handleFileChange}
                onFileDrop={handleFileDrop}
                onDragOver={handleDragOver}
                onClear={clearFile}
                hasError={!!(errors.fileBase64 || errors.fileName)}
              />
              {errors.fileName ? (
                <p className={DOCUMENT_FIELD.error}>
                  {errors.fileName.message}
                </p>
              ) : null}
              {errors.fileBase64 && !errors.fileName ? (
                <p className={DOCUMENT_FIELD.error}>
                  {errors.fileBase64.message}
                </p>
              ) : null}
            </div>
          </form>
        </div>

        <div className={DOCUMENT_MODAL.footer}>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className={DOCUMENT_BTN.modalCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit(onSubmit)}
            disabled={isSubmitting}
            className={DOCUMENT_BTN.modalPrimary}
          >
            {isSubmitting ? (
              <>
                <SpinnerIcon className="animate-spin" size={14} />
                Saving...
              </>
            ) : mode === "create" ? (
              "Create"
            ) : (
              "Save Changes"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TaxFormModal;
